import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useEmailRefreshStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { env } from '@/lib/environment';

interface SimpleRefreshStatus {
  isRefreshing: boolean;
  progress: number;
  message: string;
  error: string | null;
  taskId: string | null;
}

interface SimpleTaskStatus {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  result?: any;
  error_message?: string;
}

/**
 * Simplified email refresh hook using the new direct processing system.
 * No Celery, no Redis, just direct execution with SSE progress.
 */
export const useSimpleEmailRefresh = () => {
  const [refreshStatus, setRefreshStatus] = useState<SimpleRefreshStatus>({
    isRefreshing: false,
    progress: 0,
    message: '',
    error: null,
    taskId: null
  });
  
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Store integration for unified state
  const { 
    startRefresh: storeStartRefresh, 
    updateProgress: storeUpdateProgress,
    setError: storeSetError,
    setInfoMessage: storeSetInfoMessage,
    cancelRefresh: storeCancelRefresh,
    reset: storeReset,
    setLastRefreshDate
  } = useEmailRefreshStore();
  
  // Check for active tasks on mount
  useEffect(() => {
    checkActiveTask();
  }, []);
  
  const checkActiveTask = useCallback(async () => {
    try {
      const response = await apiRequest('GET', '/api/simple/tasks/active');
      if (response.ok) {
        const tasks = await response.json();
        const emailTask = tasks.find((task: SimpleTaskStatus) => 
          task.status === 'running'
        );
        
        if (emailTask) {
          // Resume tracking this task
          console.log(`[SimpleEmailRefresh] Resuming active task ${emailTask.id}`);
          setRefreshStatus({
            isRefreshing: true,
            progress: emailTask.progress || 0,
            message: emailTask.message || 'Resuming...',
            error: null,
            taskId: emailTask.id
          });
          
          startTaskMonitoring(emailTask.id);
        }
      }
    } catch (error) {
      console.error('[SimpleEmailRefresh] Failed to check active tasks:', error);
    }
  }, []);
  
  const startRefresh = useCallback(async (isOAuth = false, oauthProvider = 'google') => {
    try {
      console.log('[SimpleEmailRefresh] Starting simplified email refresh');
      
      // Update state
      setRefreshStatus({
        isRefreshing: true,
        progress: 0,
        message: 'Starting email refresh...',
        error: null,
        taskId: null
      });
      
      storeStartRefresh();
      storeUpdateProgress({
        stage: 'initialization',
        step: 'starting',
        current: 0,
        percent: 0,
        detail: 'Initializing email refresh...'
      });
      
      // Start refresh via new simple API
      const response = await apiRequest('POST', '/api/simple/refresh-emails', {
        isOAuth,
        oauthProvider
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || 'Failed to start email refresh';
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      const { task_id } = data;
      
      console.log(`[SimpleEmailRefresh] Task created: ${task_id}`);
      
      // Update status with task ID
      setRefreshStatus(prev => ({
        ...prev,
        taskId: task_id,
        message: 'Email refresh started'
      }));
      
      // Start monitoring the task
      startTaskMonitoring(task_id);
      
    } catch (error: any) {
      console.error('[SimpleEmailRefresh] Failed to start:', error);
      
      const errorMessage = error.message || 'Failed to start email refresh';
      
      setRefreshStatus({
        isRefreshing: false,
        progress: 0,
        message: '',
        error: errorMessage,
        taskId: null
      });
      
      storeSetError(errorMessage);
      
      toast({
        title: "Failed to Start",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [storeStartRefresh, storeUpdateProgress, storeSetError, toast]);
  
  const startTaskMonitoring = useCallback((taskId: string) => {
    console.log(`[SimpleEmailRefresh] Starting monitoring for task ${taskId}`);
    
    // Try SSE first
    if (typeof EventSource !== 'undefined') {
      try {
        const sessionId = localStorage.getItem("sessionId");
        const sseUrl = sessionId 
          ? `${env.apiUrl}/api/simple/tasks/${taskId}/stream?token=${encodeURIComponent(sessionId)}`
          : `${env.apiUrl}/api/simple/tasks/${taskId}/stream`;
        
        console.log('[SimpleEmailRefresh] Creating EventSource');
        const eventSource = new EventSource(sseUrl);
        
        eventSource.onmessage = (event) => {
          try {
            const update = JSON.parse(event.data);
            handleTaskUpdate(update);
          } catch (error) {
            console.error('[SimpleEmailRefresh] Failed to parse SSE:', error);
          }
        };
        
        eventSource.onerror = (error) => {
          console.error('[SimpleEmailRefresh] SSE error, falling back to polling:', error);
          eventSource.close();
          startPolling(taskId);
        };
        
        eventSourceRef.current = eventSource;
        return;
      } catch (error) {
        console.error('[SimpleEmailRefresh] Failed to create EventSource:', error);
      }
    }
    
    // Fall back to polling
    startPolling(taskId);
  }, []);
  
  const startPolling = useCallback((taskId: string) => {
    console.log(`[SimpleEmailRefresh] Starting polling for task ${taskId}`);
    
    const pollTask = async () => {
      try {
        const response = await apiRequest('GET', `/api/simple/tasks/${taskId}/status`);
        if (response.ok) {
          const status = await response.json();
          handleTaskUpdate(status);
        }
      } catch (error) {
        console.error('[SimpleEmailRefresh] Polling error:', error);
      }
    };
    
    // Poll immediately then at intervals
    pollTask();
    pollIntervalRef.current = setInterval(pollTask, 1000); // Poll every second
  }, []);
  
  const handleTaskUpdate = useCallback((update: SimpleTaskStatus) => {
    const isComplete = update.status === 'completed' || 
                      update.status === 'failed' || 
                      update.status === 'cancelled';
    
    console.log(`[SimpleEmailRefresh] Task update: ${update.status} ${update.progress}%`);
    
    setRefreshStatus({
      isRefreshing: !isComplete,
      progress: update.progress || 0,
      message: update.message || '',
      error: update.status === 'failed' ? (update.error_message || 'Task failed') : null,
      taskId: update.id
    });
    
    // Update store
    storeUpdateProgress({
      stage: update.status === 'running' ? 'processing' : update.status,
      step: 'ongoing',
      current: update.progress || 0,
      percent: update.progress || 0,
      detail: update.message || 'Processing...'
    });
    
    // Handle completion
    if (update.status === 'completed' && update.result) {
      const result = update.result;
      const msg = `Processed ${result.total_processed} emails, ${result.new_players} new players`;
      
      setRefreshStatus({
        isRefreshing: false,
        progress: 100,
        message: msg,
        error: null,
        taskId: null
      });
      
      storeSetInfoMessage(msg);
      setLastRefreshDate(new Date().toISOString());
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/players/'] });
      queryClient.invalidateQueries({ queryKey: ['players'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      
      toast({
        title: "Email Refresh Completed",
        description: msg,
      });
      
      // Clear after delay
      setTimeout(() => {
        setRefreshStatus({
          isRefreshing: false,
          progress: 0,
          message: '',
          error: null,
          taskId: null
        });
        storeReset();
      }, 5000);
      
      stopTaskMonitoring();
    }
    
    // Handle failure
    if (update.status === 'failed') {
      const errorMsg = update.error_message || 'Email refresh failed';
      storeSetError(errorMsg);
      toast({
        title: "Email Refresh Failed",
        description: errorMsg,
        variant: "destructive"
      });
      stopTaskMonitoring();
    }
    
    // Handle cancellation
    if (update.status === 'cancelled') {
      storeCancelRefresh();
      toast({
        title: "Email Refresh Cancelled",
        description: "The email refresh was cancelled",
      });
      stopTaskMonitoring();
    }
  }, [queryClient, storeUpdateProgress, storeSetInfoMessage, storeSetError, storeCancelRefresh, storeReset, setLastRefreshDate, toast]);
  
  const stopTaskMonitoring = useCallback(() => {
    console.log('[SimpleEmailRefresh] Stopping task monitoring');
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);
  
  const cancelRefresh = useCallback(async () => {
    if (!refreshStatus.taskId) return;
    
    try {
      console.log(`[SimpleEmailRefresh] Cancelling task ${refreshStatus.taskId}`);
      
      await apiRequest('POST', `/api/simple/tasks/${refreshStatus.taskId}/cancel`);
      
      setRefreshStatus({
        isRefreshing: false,
        progress: 0,
        message: 'Refresh cancelled',
        error: null,
        taskId: null
      });
      
      storeCancelRefresh();
      stopTaskMonitoring();
      
    } catch (error) {
      console.error('[SimpleEmailRefresh] Failed to cancel:', error);
      toast({
        title: "Failed to Cancel",
        description: "Could not cancel the email refresh",
        variant: "destructive"
      });
    }
  }, [refreshStatus.taskId, storeCancelRefresh, stopTaskMonitoring, toast]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTaskMonitoring();
    };
  }, [stopTaskMonitoring]);
  
  return {
    ...refreshStatus,
    startRefresh,
    cancelRefresh
  };
};